import React, { useState, useEffect } from "react";
import {
  ChatQuote as QuoteIcon,
  CalendarEvent as EventIcon,
  Send as SendIcon,
  CheckCircleFill as ActiveIcon,
  XCircleFill as BlockedIcon,
  QuestionCircleFill as UnverifiedIcon,
  ChevronLeft,
  ChevronRight,
  PersonFill,
  GeoAltFill,
  EnvelopeFill,
  FileTextFill,
  ClockFill
} from "react-bootstrap-icons";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  Chip,
  Avatar,
  Tooltip as MuiTooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button as MuiButton,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Pagination as MuiPagination
} from "@mui/material";
import axios from "axios";
import motivationalImage4 from "./images/motivational-reminder4.jpg";
import eventImage1 from "./images/event-template1.png";
import eventImage2 from "./images/event-template2.png";
import eventImage3 from "./images/event-template3.png";
import eventImage4 from "./images/event-template4.png";
import inspirationImage1 from "./images/inspirationImage1.png";
import inspirationImage2 from "./images/inspirationImage2.png";
import inspirationImage3 from "./images/inspirationImage1.png";
import {
  eventTemplate1,
  eventTemplate2,
  eventTemplate3,
  eventTemplate4,
  reminderTemplate1,
  reminderTemplate2,
  reminderTemplate3,
  reminderTemplate4,
} from "./template";
import API_BASE_URL from "../../../config/api";
import { getAdminAuthHeader } from "../../../services/auth";

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  minWidth: "120px",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const templateMap = {
  "quote-1": reminderTemplate1,
  "quote-2": reminderTemplate2,
  "quote-3": reminderTemplate3,
  "quote-4": reminderTemplate4,
  "event-1": eventTemplate1,
  "event-2": eventTemplate2,
  "event-3": eventTemplate3,
  "event-4": eventTemplate4,
};

/* ── Moved OUTSIDE the parent so it never remounts on re-render ── */
const EmailTemplateViewer = ({
  templateId,
  title,
  message,
  date,
  time,
  className,
  instructor,
  location,
}) => {
  const templateFunction = templateMap[templateId] || reminderTemplate1;
  return (
    <Paper
      sx={{
        height: "100%",
        minHeight: "400px",
        overflow: "auto",
        borderRadius: "12px",
        border: "1px solid #e4eaf8",
        boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
      }}
    >
      <div
        dangerouslySetInnerHTML={{
          __html: templateFunction({
            title,
            message,
            date,
            time,
            className,
            instructor,
            location,
          }),
        }}
      />
    </Paper>
  );
};

const NotificationCreator = () => {
  // Color variables
  const colors = {
    primary: "#0d47a1",
    secondary: "#1a237e",
    accent: "#f0f4ff",
    textPrimary: "#1a237e",
    success: "#2e7d32",
    danger: "#c62828",
    warning: "#f57f17",
    info: "#00acc1",
  };

  const [notificationType, setNotificationType] = useState("quote");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [locations, setLocations] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [recipientType, setRecipientType] = useState("user");
  const [location, setLocation] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    date: "",
    time: "",
    className: "",
    instructor: "",
    location: "",
  });
  const [paginationData, setPaginationData] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });

  useEffect(() => {
    if (notificationType === "event") {
      fetchUpcomingEvents();
    }
  }, [notificationType]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/location/admin/getAllLocations`
        );
        setLocations(
          Array.isArray(response.data.data) ? response.data.data : []
        );
      } catch (error) {
        console.error("Error fetching locations:", error);
        setLocations([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const getNextUpcomingSession = (event) => {
    if (!event?.sessions || !Array.isArray(event.sessions)) return null;
    const now = new Date();
    const upcomingSessions = event.sessions
      .filter((session) => new Date(session.date) > now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return upcomingSessions.length > 0 ? upcomingSessions[0] : null;
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const fetchRecipients = async (page = 1) => {
    if (!recipientType) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      let url = `${API_BASE_URL}/api/admin/getRegister/${recipientType}?page=${page}&limit=${itemsPerPage}`;
      const eventLocation = selectedEvent?.location?.location;
      if (notificationType === "event" && eventLocation) {
        url += `&filterLocation=${eventLocation}`;
      } else if (location) {
        url += `&filterLocation=${location}`;
      }
      if (statusFilter && statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const users = Array.isArray(response.data.users) ? response.data.users : [];
      const updatedUsers = users.map((user) => {
        const existingUser = recipients.find((r) => r._id === user._id);
        return {
          ...user,
          selected: existingUser ? existingUser.selected : false,
          status: user.accountStatus || "active",
        };
      });
      setRecipients(updatedUsers);
      setPaginationData({
        total: response.data.total || 0,
        page: response.data.page || 1,
        limit: response.data.limit || 10,
        totalPages: Math.ceil((response.data.total || 0) / (response.data.limit || 10)),
      });
    } catch (error) {
      console.error(`Error fetching ${recipientType}s:`, error);
      setRecipients([]);
      setPaginationData({ total: 0, page: 1, limit: 10, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipients(currentPage);
  }, [recipientType, location, notificationType, selectedEvent, statusFilter]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchRecipients(pageNumber);
  };

  const fetchUpcomingEvents = async () => {
    try {
      setLoadingEvents(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/AdminClasses/getAllUpcomingClasses`,
        { headers: getAdminAuthHeader() }
      );
      setUpcomingEvents(response.data.data || []);
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const templates = {
    quote: [
      { id: "quote-1", name: "Daily Motivation", description: "Inspire students with positive quotes", image: inspirationImage1 },
      { id: "quote-2", name: "Exam Encouragement", description: "Boost morale before tests", image: inspirationImage2 },
      { id: "quote-3", name: "Weekly Inspiration", description: "Motivational quotes for the week", image: inspirationImage3 },
      { id: "quote-4", name: "Success Tips", description: "Tips for academic success", image: motivationalImage4 },
    ],
    event: [
      { id: "event-1", name: "Event Invitation", description: "Invite students to special events", image: eventImage1 },
      { id: "event-2", name: "Workshop Alert", description: "Announce skill-building sessions", image: eventImage2 },
      { id: "event-3", name: "Seminar Notification", description: "Information about upcoming seminars", image: eventImage3 },
      { id: "event-4", name: "Conference Reminder", description: "Reminder for important conferences", image: eventImage4 },
    ],
  };

  const getLocationName = (loc) => {
    if (!loc || typeof loc !== "object") return loc || "N/A";
    return loc.location || "N/A";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventChange = (e) => {
    const eventId = e.target.value;
    const selected = upcomingEvents.find((event) => event._id === eventId);
    setSelectedEvent(selected);
    if (selected) {
      const nextSession = getNextUpcomingSession(selected);
      const formattedDate = nextSession
        ? formatDisplayDate(nextSession.date)
        : selected.startDate
        ? formatDisplayDate(selected.startDate)
        : "Not specified";
      const formattedTime =
        nextSession && nextSession.startTime && nextSession.endTime
          ? `${nextSession.startTime} - ${nextSession.endTime}`
          : "Not specified";
      setFormData({
        title: selected.title || "Class Invitation",
        message: selected.theme || selected.description || `Join us for this special class`,
        date: formattedDate,
        time: formattedTime,
        className: selected.title || "Class details coming soon",
        instructor: selected.Instructor?.name || "Instructor not assigned",
        location: selected.location?.location || "Location to be announced",
      });
    }
  };

  const getNotificationTypeName = (type) => {
    switch (type) {
      case "quote": return "Inspirational Quote";
      case "event": return "Upcoming Event Invitation";
      default: return type;
    }
  };

  const getTemplateName = (templateId) => {
    const templateNumber = templateId.split("-")[1];
    return `template${templateNumber}`;
  };

  const templateFunction = templateMap[selectedTemplate?.id || "quote-1"];
  const htmlContent = templateFunction({
    title: formData.title,
    message: formData.message,
    date: formData.date,
    time: formData.time,
    className: formData.className,
    instructor: formData.instructor,
    location: formData.location,
  });

  const toggleUserSelection = (id) => {
    setRecipients(
      recipients.map((recipient) =>
        (recipient.id || recipient._id) === id
          ? { ...recipient, selected: !recipient.selected }
          : recipient
      )
    );
  };

  const toggleSelectAll = (e) => {
    const isChecked = e.target.checked;
    setRecipients(recipients.map((recipient) => ({ ...recipient, selected: isChecked })));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active": return <ActiveIcon className="text-success" size={14} />;
      case "blocked": return <BlockedIcon className="text-danger" size={14} />;
      case "unverified": return <UnverifiedIcon className="text-warning" size={14} />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return colors.success;
      case "blocked": return colors.danger;
      case "unverified": return colors.warning;
      default: return colors.info;
    }
  };

  const getEventDisplayInfo = (event) => {
    const nextSession = getNextUpcomingSession(event);
    if (nextSession) {
      const formattedDate = formatDisplayDate(nextSession.date);
      const timeStr =
        nextSession.startTime && nextSession.endTime
          ? `${nextSession.startTime} - ${nextSession.endTime}`
          : "Time TBD";
      return `${event.title || event.eventName} - ${formattedDate} at ${timeStr}`;
    } else if (event.startDate) {
      return `${event.title || event.eventName} - Starts ${formatDisplayDate(event.startDate)}`;
    }
    return `${event.title || event.eventName}`;
  };

  const handleSubmit = async () => {
    try {
      setSending(true);
      if (notificationType === "event" && recipientType === "mentor") {
        alert("Class invitations cannot be sent to mentors");
        return;
      }
      const selectedUserIds = recipients
        .filter((recipient) => recipient.selected)
        .map((recipient) => recipient.id || recipient._id);
      if (selectedUserIds.length === 0) {
        alert("Please select at least one recipient");
        return;
      }
      const notificationData = {
        notificationType: getNotificationTypeName(notificationType),
        template: selectedTemplate ? getTemplateName(selectedTemplate.id) : "template1",
        title: formData?.title,
        message: formData?.message,
        html: htmlContent,
        users: selectedUserIds,
      };
      if (notificationType === "event" && selectedEvent) {
        notificationData.classId = selectedEvent._id;
        const nextSession = getNextUpcomingSession(selectedEvent);
        if (nextSession) {
          notificationData.sessionDate = nextSession.date;
          notificationData.sessionStartTime = nextSession.startTime;
          notificationData.sessionEndTime = nextSession.endTime;
        }
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/notification/send`,
        notificationData,
        { headers: getAdminAuthHeader() }
      );
      if (res.status === 201) {
        alert("Notification created successfully!");
        setFormData({ title: "", message: "", date: "", time: "", className: "", instructor: "", location: "" });
        setSelectedEvent(null);
        setSelectedTemplate(null);
        setRecipients(recipients.map((r) => ({ ...r, selected: false })));
        setCurrentPage(1);
        setPaginationData({ total: 0, page: 1, limit: 10, totalPages: 1 });
      }
    } catch (error) {
      console.log(error);
      alert("Error creating notification: " + (error.response?.data?.message || error.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">Dashboard</Link>
        <Typography color="primary" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
          Create Notification
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box display="flex" alignItems="center" mb={3} gap={1.5}>
        <Box
          sx={{
            width: 42, height: 42,
            borderRadius: "11px",
            background: primaryGradient,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
          }}
        >
          <SendIcon size={20} color="#fff" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}>
            Create New Notification
          </Typography>
          <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
            Send messages and event invitations to users
          </Typography>
        </Box>
      </Box>

      {/* Notification Type Selector */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "12px", border: "1px solid #e4eaf8", boxShadow: "0 2px 12px rgba(13,71,161,0.06)" }}>
        <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 2 }}>SELECT NOTIFICATION TYPE</Typography>
        <FormControl fullWidth sx={{ maxWidth: 400 }}>
          <Select
            value={notificationType}
            onChange={(e) => {
              const selected = e.target.value;
              setNotificationType(selected);
              setSelectedTemplate(null);
              setSelectedEvent(null);
              setFormData({ title: "", message: "", date: "", time: "", className: "", instructor: "", location: "" });
            }}
            sx={{
              ...fieldStyle,
              "& .MuiOutlinedInput-notchedOutline": { border: "1.5px solid #dde4f5" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#0d47a1" },
            }}
          >
            <MenuItem value="quote">Inspirational Quote</MenuItem>
            <MenuItem value="event">Upcoming Event Invitation</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Template Selection */}
      {notificationType && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: "12px", border: "1px solid #e4eaf8", boxShadow: "0 2px 12px rgba(13,71,161,0.06)" }}>
          <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 3 }}>CHOOSE A TEMPLATE</Typography>
          <Grid container spacing={2}>
            {templates[notificationType].map((template) => (
              <Grid item xs={12} sm={6} md={3} key={template.id}>
                <Card
                  onClick={() => setSelectedTemplate(template)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "12px",
                    border: selectedTemplate?.id === template.id ? "2px solid #0d47a1" : "1px solid #e4eaf8",
                    transition: "all 0.2s ease",
                    "&:hover": { transform: "scale(1.02)", boxShadow: "0 4px 20px rgba(13,71,161,0.15)" },
                  }}
                >
                  <CardActionArea>
                    <CardMedia component="img" height="120" image={template.image} alt={template.name} sx={{ objectFit: "cover" }} />
                    <CardContent sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1a237e" }}>{template.name}</Typography>
                      <Typography variant="caption" sx={{ color: "#7b8ab8" }}>{template.description}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Event Selection + Preview */}
     {selectedTemplate && notificationType === "event" && (
  <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
    <Grid item xs={12} md={6}>
      <Paper
        sx={{
          p: 3, height: "100%",
          borderRadius: "12px", border: "1px solid #e4eaf8",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 3 }}>Select Event</Typography>
        {loadingEvents ? (
          <Box display="flex" justifyContent="center" py={3}>
            <div className="spinner-border" style={{ color: "#0d47a1" }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </Box>
        ) : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <Select
                value={selectedEvent?._id || ""}
                onChange={handleEventChange}
                sx={{
                  ...fieldStyle,
                  "& .MuiOutlinedInput-notchedOutline": { border: "1.5px solid #dde4f5" },
                }}
              >
                <MenuItem value="">Select an event...</MenuItem>
                {upcomingEvents.map((event) => (
                  <MenuItem key={event._id} value={event._id}>
                    {getEventDisplayInfo(event)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date and Time Input Fields - Added for Event Templates */}
            <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 2, mt: 2 }}>
              Or Customize Date & Time
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true, sx: labelStyle }}
                  InputProps={{ sx: fieldStyle }}
                  helperText="Override event date"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true, sx: labelStyle }}
                  InputProps={{ sx: fieldStyle }}
                  helperText="Override event time"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location (Override)"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  InputProps={{ sx: fieldStyle }}
                  InputLabelProps={{ sx: labelStyle }}
                  placeholder="Override event location"
                  helperText="Optional: Override the event location"
                />
              </Grid>
            </Grid>

            {/* Display current event details if available */}
            {selectedEvent && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "#f0f4ff", borderRadius: "8px" }}>
                <Typography variant="caption" sx={{ color: "#1a237e", fontWeight: 600 }}>
                  Current Event Details:
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: "#6b7a99", mt: 0.5 }}>
                  Date: {formData.date || "Not set"}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: "#6b7a99" }}>
                  Time: {formData.time || "Not set"}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: "#6b7a99" }}>
                  Location: {formData.location || "Not set"}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Grid>
    <Grid item xs={12} md={6}>
      <EmailTemplateViewer
        templateId={selectedTemplate.id}
        title={formData.title}
        message={formData.message}
        date={formData.date}
        time={formData.time}
        className={formData.className}
        instructor={formData.instructor}
        location={formData.location}
      />
    </Grid>
  </Grid>
)}
      {/* Quote form + Preview */}
      {selectedTemplate && notificationType === "quote" && (
        <Grid container spacing={3} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3, height: "100%",
                borderRadius: "12px", border: "1px solid #e4eaf8",
                boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
                boxSizing: "border-box",
              }}
            >
              <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 3 }}>Notification Details</Typography>
              <Box>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  sx={{ mb: 2 }}
                  InputProps={{ sx: fieldStyle }}
                  InputLabelProps={{ sx: labelStyle }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  InputProps={{ sx: fieldStyle }}
                  InputLabelProps={{ sx: labelStyle }}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <EmailTemplateViewer
              templateId={selectedTemplate.id}
              title={formData.title}
              message={formData.message}
              date={formData.date}
              time={formData.time}
            />
          </Grid>
        </Grid>
      )}

      {/* Recipient Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: "12px", border: "1px solid #e4eaf8", boxShadow: "0 2px 12px rgba(13,71,161,0.06)" }}>
        <Typography sx={{ fontWeight: 600, color: "#1a237e", mb: 3 }}>Recipient Selection</Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography sx={{ ...labelStyle, mb: 1 }}>Recipient Type</Typography>
            <Box display="flex" gap={1}>
              {["user", "instructor"].map((type) => (
                <MuiButton
                  key={type}
                  variant={recipientType === type ? "contained" : "outlined"}
                  onClick={() => { setRecipientType(type); setCurrentPage(1); }}
                  sx={{
                    flex: 1, textTransform: "capitalize",
                    backgroundColor: recipientType === type ? "#0d47a1" : "transparent",
                    color: recipientType === type ? "#fff" : "#1a237e",
                    borderColor: "#0d47a1",
                    "&:hover": { backgroundColor: recipientType === type ? "#0d47a1" : "rgba(13,71,161,0.04)" },
                  }}
                >
                  {type}
                </MuiButton>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography sx={{ ...labelStyle, mb: 1 }}>Location</Typography>
            <FormControl fullWidth>
              <Select
                value={location}
                onChange={(e) => { setLocation(e.target.value); setCurrentPage(1); }}
                disabled={loading}
                sx={fieldStyle}
                placeholder="Filter by location"
              >
                <MenuItem value="all">All Locations</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.location}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography sx={{ ...labelStyle, mb: 1 }}>Status</Typography>
            <FormControl fullWidth>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                sx={fieldStyle}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <div className="spinner-border" style={{ color: "#0d47a1" }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </Box>
        ) : (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#1a237e" }}>
                {recipientType.charAt(0).toUpperCase() + recipientType.slice(1)} List
              </Typography>
              <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, paginationData.total)} of{" "}
                {paginationData.total} {recipientType}s
              </Typography>
            </Box>

            <Paper sx={{ overflow: "auto", borderRadius: "8px", border: "1px solid #e4eaf8" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: primaryGradient }}>
                    <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem" }}>
                      <input
                        type="checkbox"
                        onChange={toggleSelectAll}
                        checked={recipients.length > 0 && recipients.every((r) => r.selected)}
                      />
                    </th>
                    <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem", textAlign: "left" }}>Name</th>
                    <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem", textAlign: "left" }}>Email</th>
                    {recipientType !== "user" && (
                      <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem", textAlign: "left" }}>Specialization</th>
                    )}
                    <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem", textAlign: "left" }}>Location</th>
                    <th style={{ padding: "12px", color: "#fff", fontWeight: 600, fontSize: "0.75rem", textAlign: "left" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.length > 0 ? (
                    recipients.map((recipient, index) => (
                      <tr key={recipient._id || index} style={{ borderBottom: "1px solid #f0f4ff" }}>
                        <td style={{ padding: "12px" }}>
                          <input
                            type="checkbox"
                            checked={recipient.selected || false}
                            onChange={() => toggleUserSelection(recipient._id)}
                          />
                        </td>
                        <td style={{ padding: "12px", color: "#1a237e", fontWeight: 500, fontSize: "0.85rem" }}>
                          {recipient.name || "N/A"}
                        </td>
                        <td style={{ padding: "12px", color: "#6b7a99", fontSize: "0.8rem" }}>
                          {recipient.email || "N/A"}
                        </td>
                        {recipientType !== "user" && (
                          <td style={{ padding: "12px", color: "#6b7a99", fontSize: "0.8rem" }}>
                            {recipient.specialization || recipient.expertise || "N/A"}
                          </td>
                        )}
                        <td style={{ padding: "12px", color: "#6b7a99", fontSize: "0.8rem" }}>
                          <GeoAltFill size={12} style={{ marginRight: "4px", color: "#0d47a1" }} />
                          {getLocationName(recipient.location)}
                        </td>
                        <td style={{ padding: "12px" }}>
                          <Chip
                            size="small"
                            icon={getStatusIcon(recipient.status || "active")}
                            label={recipient.status || "active"}
                            sx={{
                              backgroundColor: `${getStatusColor(recipient.status || "active")}20`,
                              color: getStatusColor(recipient.status || "active"),
                              fontWeight: 600, fontSize: "0.7rem",
                              "& .MuiChip-icon": { color: getStatusColor(recipient.status || "active") },
                            }}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={recipientType !== "user" ? 6 : 5}
                        style={{ padding: "24px", textAlign: "center", color: "#7b8ab8" }}
                      >
                        No {recipientType}s found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </Paper>

            {paginationData.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <MuiPagination
                  count={paginationData.totalPages}
                  page={currentPage}
                  onChange={(e, page) => paginate(page)}
                  color="primary"
                  size="small"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#1a237e",
                      "&.Mui-selected": {
                        backgroundColor: "#0d47a1",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#0d47a1" },
                      },
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Submit Button */}
      <MuiButton
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={(notificationType === "event" && !selectedEvent) || sending}
        sx={{
          py: 1.5,
          background: primaryGradient,
          color: "#fff",
          fontWeight: 600,
          borderRadius: "10px",
          boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
          "&:hover": { background: primaryGradient, boxShadow: "0 6px 20px rgba(13,71,161,0.4)" },
          "&:disabled": { opacity: 0.6 },
        }}
      >
        {sending ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
            Sending...
          </>
        ) : (
          <>
            <SendIcon size={18} className="me-2" />
            Send Notification
          </>
        )}
      </MuiButton>
    </Box>
  );
};

export default NotificationCreator;
